/**
 * Sales KPI derivation chain for the forecast summary grid.
 *
 * These definitions are passed to the edit propagation engine's
 * `recalcDerivedMetrics` function. Order matters — later entries
 * can reference values computed by earlier ones.
 */

import type { DerivedMetricDef, KpiFlow } from "./editPropagation";
import { coverDivide, safeDivide } from "./editPropagation";

/**
 * Stock metric KPI flows — define endpoint rules for TTL derivation:
 * BOP from PTD, EOP/TS from PTG, OTB calculated per flow.
 * Processed in order: UNIT → AT_COST → AUC (AUC reads earlier OTB values).
 */
export const STOCK_KPI_FLOWS: KpiFlow[] = [
  {
    bop: "BEGINNING_OF_PERIOD_UNITS",
    eop: "END_OF_PERIOD_UNITS",
    ts: "TARGET_STOCK_UNITS_TOTAL",
    otb: "OPEN_TO_BUY_UNITS",
    calcOtb: (v) =>
      (v["TARGET_STOCK_UNITS_TOTAL"] ?? 0) - (v["END_OF_PERIOD_UNITS"] ?? 0)
  },
  {
    bop: "BEGINNING_OF_PERIOD_AT_COST",
    eop: "END_OF_PERIOD_AT_COST",
    ts: "TARGET_STOCK_AT_COST",
    otb: "OPEN_TO_BUY_AT_COST",
    calcOtb: (v) =>
      (v["TARGET_STOCK_AT_COST"] ?? 0) - (v["END_OF_PERIOD_AT_COST"] ?? 0)
  },
  {
    bop: "BEGINNING_OF_PERIOD_AUC",
    eop: "END_OF_PERIOD_AUC",
    ts: "TARGET_STOCK_AUC",
    otb: "OPEN_TO_BUY_AUC",
    calcOtb: (v) =>
      safeDivide(v["OPEN_TO_BUY_AT_COST"] ?? 0, v["OPEN_TO_BUY_UNITS"] ?? 0)
  }
];

/** Intake sub-component metrics (for toggle and editability checks). */
export const INTAKE_COMPONENT_METRICS = new Set([
  "INTAKE_UNITS_MAIN",
  "INTAKE_UNITS_REPLENISHMENT",
  "INTAKE_UNITS_INSEASON",
  "INTAKE_UNITS_CLEARANCE",
  "INTAKE_AT_COST_MAIN",
  "INTAKE_AT_COST_REPLENISHMENT",
  "INTAKE_AT_COST_INSEASON",
  "INTAKE_AT_COST_CLEARANCE"
]);

/**
 * Columns holding the displayed year's opening stock (`BOP|PTD`).
 * For future years the opening is rolled server-side through the weeks between
 * the last actual and the year start, and differs between planned and
 * confirmed intake — the intake toggle swaps these columns in the server
 * layer, and the stock balance re-derives the rest at render. Matches the
 * backend's `_get_intake_toggle_columns` opening set.
 */
export const OPENING_STOCK_COLUMNS = new Set(
  STOCK_KPI_FLOWS.map((flow) => `${flow.bop}|PTD`)
);

/**
 * FURTHER_MD_* metrics that are NOT safely summable on temporally aggregated
 * views: the four base leaves whose units are re-counted when a style option is
 * marked down in multiple weeks (`NEW MD` units move from the full-price to
 * the markdown bucket; `FURTHER MD` units stay there and deepen the discount),
 * plus every ratio derived from them (their numerator and/or denominator
 * inherits the over-count). Only FURTHER_MD_VALUE_MARGINAL (the additive
 * marginal reduction) is safe and can stay visible. See
 * specs/domain/WIP/planned_markdowns/planned-markdowns.md §KPIs.
 */
export const NON_SUMMABLE_MARKDOWN_METRICS = [
  "FURTHER_MD_UNITS",
  "FURTHER_MD_FULL_PRICE",
  "FURTHER_MD_COST",
  "FURTHER_MD_VALUE_TOTAL",
  "FURTHER_MD_PERCENTAGE_MARGINAL",
  "FURTHER_MD_PERCENTAGE_TOTAL",
  "FURTHER_MD_AUV",
  "FURTHER_MD_AUR"
];

const SALES_KPI_DERIVATIONS_BASE: DerivedMetricDef[] = [
  // Net items / net sales (subtraction)
  { name: "NIS3", calc: (v) => (v["GIS1"] ?? 0) - (v["GIR"] ?? 0) },
  { name: "NS3", calc: (v) => (v["GS2"] ?? 0) - (v["GR2"] ?? 0) },
  { name: "NS3_COGS", calc: (v) => (v["GS1_COGS"] ?? 0) - (v["COGR"] ?? 0) },

  // Average unit retail / cost (division)
  { name: "GS1_AUR", calc: (v) => safeDivide(v["GS1"] ?? 0, v["GIS1"] ?? 0) },
  {
    name: "GS1_AUC",
    calc: (v) => safeDivide(v["GS1_COGS"] ?? 0, v["GIS1"] ?? 0)
  },
  { name: "GS2_AUR", calc: (v) => safeDivide(v["GS2"] ?? 0, v["GIS1"] ?? 0) },
  { name: "GR2_AUR", calc: (v) => safeDivide(v["GR2"] ?? 0, v["GIR"] ?? 0) },
  { name: "GR2_AUC", calc: (v) => safeDivide(v["COGR"] ?? 0, v["GIR"] ?? 0) },
  { name: "NS3_AUR", calc: (v) => safeDivide(v["NS3"] ?? 0, v["NIS3"] ?? 0) },
  {
    // NaN (rendered blank) when underivable, matching calculate_ns_iii_auc:
    // a value exists only when NIS3 is non-zero and the inputs are present.
    name: "NS3_AUC",
    calc: (v) =>
      (v["NIS3"] ?? 0) === 0
        ? Number.NaN
        : ((v["GS1_COGS"] ?? 0) - (v["COGR"] ?? 0)) / (v["NIS3"] ?? 0)
  },

  // Margins
  {
    name: "GS2_MARGIN_VALUE",
    calc: (v) => (v["GS2"] ?? 0) - (v["GS1_COGS"] ?? 0)
  },
  {
    name: "GS2_MARGIN_PERCENTAGE",
    calc: (v) => safeDivide(v["GS2_MARGIN_VALUE"] ?? 0, v["GS2"] ?? 0)
  },
  {
    name: "NS3_MARGIN_VALUE",
    calc: (v) => (v["NS3"] ?? 0) - (v["NS3_COGS"] ?? 0)
  },
  {
    name: "NS3_MARGIN_PERCENTAGE",
    calc: (v) => safeDivide(v["NS3_MARGIN_VALUE"] ?? 0, v["NS3"] ?? 0)
  },

  // Discount
  { name: "GS_DISCOUNT_COST", calc: (v) => (v["GS1"] ?? 0) - (v["GS2"] ?? 0) },
  {
    // 1 - GS2/GS1 (not discount/GS1): matches calculate_gs_discount_percent
    // and the cascade's calcGsDiscountPct — the two differ when GS1 is 0
    // (this form yields 1, the ratio form yielded 0).
    name: "GS_DISCOUNT_PERCENTAGE",
    calc: (v) => 1 - safeDivide(v["GS2"] ?? 0, v["GS1"] ?? 0)
  },

  // Return percentages
  {
    name: "RETURN_UNIT_PERCENTAGE",
    calc: (v) => safeDivide(v["GIR"] ?? 0, v["GIS1"] ?? 0)
  },
  {
    name: "RETURN_VALUE_PERCENTAGE",
    calc: (v) => safeDivide(v["GR2"] ?? 0, v["GS2"] ?? 0)
  },
  {
    name: "RETURN_COST_PERCENTAGE",
    calc: (v) => safeDivide(v["COGR"] ?? 0, v["GS1_COGS"] ?? 0)
  },

  // Planned markdowns (DAS-6372) — context-free ratios over the irreducible
  // NEW/FURTHER markdown metrics. Mirror the Python KPI engine (DAS-6371) and
  // specs/domain/WIP/planned_markdowns/planned-markdowns.md §KPIs. The
  // context-dependent EOP units / @ cost correction is applied via
  // buildSalesKpiDerivations (below); EOP @ Retail / NEW_MD TOTAL halving remain
  // server-side.
  {
    name: "NEW_MD_PERCENTAGE",
    calc: (v) => leafRatio(v["NEW_MD_VALUE_TOTAL"], v["NEW_MD_FULL_PRICE"])
  },
  {
    name: "NEW_MD_AUV",
    calc: (v) => leafRatio(v["NEW_MD_VALUE_TOTAL"], v["NEW_MD_UNITS"])
  },
  {
    name: "NEW_MD_AUR",
    calc: (v) =>
      leafRatio(
        difference(v["NEW_MD_FULL_PRICE"], v["NEW_MD_VALUE_TOTAL"]),
        v["NEW_MD_UNITS"]
      )
  },
  {
    name: "FURTHER_MD_PERCENTAGE_MARGINAL",
    calc: (v) =>
      leafRatio(v["FURTHER_MD_VALUE_MARGINAL"], v["FURTHER_MD_FULL_PRICE"])
  },
  {
    name: "FURTHER_MD_PERCENTAGE_TOTAL",
    calc: (v) =>
      leafRatio(v["FURTHER_MD_VALUE_TOTAL"], v["FURTHER_MD_FULL_PRICE"])
  },
  {
    name: "FURTHER_MD_AUV",
    calc: (v) => leafRatio(v["FURTHER_MD_VALUE_TOTAL"], v["FURTHER_MD_UNITS"])
  },
  {
    name: "FURTHER_MD_AUR",
    calc: (v) =>
      leafRatio(
        difference(v["FURTHER_MD_FULL_PRICE"], v["FURTHER_MD_VALUE_TOTAL"]),
        v["FURTHER_MD_UNITS"]
      )
  },

  // Intake totals
  {
    name: "INTAKE_UNITS_TOTAL",
    calc: (v) =>
      (v["INTAKE_UNITS_MAIN"] ?? 0) +
      (v["INTAKE_UNITS_REPLENISHMENT"] ?? 0) +
      (v["INTAKE_UNITS_INSEASON"] ?? 0) -
      (v["INTAKE_UNITS_CLEARANCE"] ?? 0)
  },
  {
    name: "INTAKE_AT_COST_TOTAL",
    calc: (v) =>
      (v["INTAKE_AT_COST_MAIN"] ?? 0) +
      (v["INTAKE_AT_COST_REPLENISHMENT"] ?? 0) +
      (v["INTAKE_AT_COST_INSEASON"] ?? 0) -
      (v["INTAKE_AT_COST_CLEARANCE"] ?? 0)
  },
  {
    name: "INTAKE_AUC_TOTAL",
    calc: (v) =>
      safeDivide(v["INTAKE_AT_COST_TOTAL"] ?? 0, v["INTAKE_UNITS_TOTAL"] ?? 0)
  },
  ...["MAIN", "REPLENISHMENT", "INSEASON", "CLEARANCE"].map(
    function bucketAuc(bucket): DerivedMetricDef {
      return {
        name: `INTAKE_AUC_${bucket}`,
        calc: (v) =>
          safeDivide(
            v[`INTAKE_AT_COST_${bucket}`] ?? 0,
            v[`INTAKE_UNITS_${bucket}`] ?? 0
          )
      };
    }
  ),

  // Stock levels reconstructed from the balance EOP = BOP + INTAKE - SALES
  // (and OTB = TS - EOP). Flagged `stockBalanceDerived`: the balance only
  // holds where intake is still tracked as intake (PTG, and a forecast PTD),
  // so these are applied there and taken from the server's point-in-time value
  // elsewhere. See `DerivedMetricDef.stockBalanceDerived`.
  {
    name: "END_OF_PERIOD_UNITS",
    calc: (v) =>
      (v["BEGINNING_OF_PERIOD_UNITS"] ?? 0) +
      (v["INTAKE_UNITS_TOTAL"] ?? 0) -
      (v["NIS3"] ?? 0),
    stockBalanceDerived: true
  },
  {
    name: "END_OF_PERIOD_AT_COST",
    calc: (v) =>
      (v["BEGINNING_OF_PERIOD_AT_COST"] ?? 0) +
      (v["INTAKE_AT_COST_TOTAL"] ?? 0) -
      (v["NS3_COGS"] ?? 0),
    stockBalanceDerived: true
  },
  {
    name: "END_OF_PERIOD_AUC",
    calc: (v) =>
      safeDivide(
        v["END_OF_PERIOD_AT_COST"] ?? 0,
        v["END_OF_PERIOD_UNITS"] ?? 0
      ),
    stockBalanceDerived: true
  },
  // OTB = TS - EOP
  {
    name: "OPEN_TO_BUY_UNITS",
    calc: (v) =>
      (v["TARGET_STOCK_UNITS_TOTAL"] ?? 0) - (v["END_OF_PERIOD_UNITS"] ?? 0),
    stockBalanceDerived: true
  },
  {
    name: "OPEN_TO_BUY_AT_COST",
    calc: (v) =>
      (v["TARGET_STOCK_AT_COST"] ?? 0) - (v["END_OF_PERIOD_AT_COST"] ?? 0),
    stockBalanceDerived: true
  },
  {
    name: "OPEN_TO_BUY_AUC",
    calc: (v) =>
      safeDivide(v["OPEN_TO_BUY_AT_COST"] ?? 0, v["OPEN_TO_BUY_UNITS"] ?? 0),
    stockBalanceDerived: true
  }
];

/**
 * Planned OTB — the intake that would drive OTB to zero.
 *
 * Deliberately NOT in {@link SALES_KPI_DERIVATIONS}: POTB units / at-cost are
 * the running-max increment of the weekly OTB series (see the weekly engine's
 * `fillPotb`), which no aggregate slice can reconstruct — a period's POTB needs
 * the OTB *maximum* over the period, and the summary only holds its endpoints.
 * So on the summary POTB is server-owned and {@link POTB_METRICS} blanks it on
 * edit; only the temporal grid, which has the weekly frame, restates it live.
 *
 * The AUC is the exception: a ratio of the summed units and at-cost, so it is
 * recomputed wherever those two are aggregated ({@link computeTemporalAggregates}).
 */
export const POTB_AUC_DERIVATION: DerivedMetricDef = {
  name: "PLANNED_OPEN_TO_BUY_AUC",
  calc: (v) =>
    safeDivide(
      v["PLANNED_OPEN_TO_BUY_AT_COST"] ?? 0,
      v["PLANNED_OPEN_TO_BUY_UNITS"] ?? 0
    )
};

/**
 * Metrics an unsaved client edit invalidates: the grid cannot restate them, so
 * they are blanked rather than left showing a number the edit already broke.
 * See {@link POTB_AUC_DERIVATION} for why POTB cannot be restated here.
 */
export const POTB_METRICS = [
  "PLANNED_OPEN_TO_BUY_UNITS",
  "PLANNED_OPEN_TO_BUY_AT_COST",
  "PLANNED_OPEN_TO_BUY_AUC",
  "PLANNED_OPEN_TO_BUY_UNITS_PER_SQM",
  "PLANNED_OPEN_TO_BUY_AT_COST_PER_SQM"
];

/**
 * The derivation set, with the context-dependent planned-markdown EOP correction
 * folded in. A NEW markdown moves units FP->MD, so full-price EOP drops by
 * NEW_MD and markdown EOP rises by it; `markdownSign` (see `markdownFlowSign`,
 * -1 / +1 / 0) encodes the selected markdown_status. `sign * NEW_MD_*` is
 * correct at any granularity because the balance equation is linear and NEW_MD_*
 * sum temporally. Mirrors the server's EOP/BOP fold (`calculate_eop_bop_numba`).
 * `markdownSign = 0` (the default / `SALES_KPI_DERIVATIONS`) is a no-op, so
 * existing consumers — incl. `temporalAggregation`, which skips the ptdPtgOnly
 * EOP derivations entirely — are unaffected.
 */
export function buildSalesKpiDerivations(markdownSign = 0): DerivedMetricDef[] {
  if (markdownSign === 0) return SALES_KPI_DERIVATIONS_BASE;
  return SALES_KPI_DERIVATIONS_BASE.map(function withMarkdown(def) {
    if (def.name === "END_OF_PERIOD_UNITS") {
      return {
        ...def,
        calc: (v) => def.calc(v) + markdownSign * (v["NEW_MD_UNITS"] ?? 0)
      };
    }
    if (def.name === "END_OF_PERIOD_AT_COST") {
      return {
        ...def,
        calc: (v) => def.calc(v) + markdownSign * (v["NEW_MD_COST"] ?? 0)
      };
    }
    return def;
  });
}

/**
 * A markdown ratio as the engine's `safe_divide` derives it — 0 on a zero
 * base — but blank when the frame lacks a leaf: nothing to derive from.
 */
function leafRatio(
  numerator: number | undefined,
  denominator: number | undefined
): number {
  return numerator === undefined || denominator === undefined
    ? Number.NaN
    : safeDivide(numerator, denominator);
}

function difference(
  a: number | undefined,
  b: number | undefined
): number | undefined {
  return a === undefined || b === undefined ? undefined : a - b;
}

/** Default derivation set with no markdown correction (markdownSign = 0). */
export const SALES_KPI_DERIVATIONS: DerivedMetricDef[] =
  buildSalesKpiDerivations();

/**
 * Rate-of-sales metric → the sales metric it averages over the period's weeks.
 * Mirrors the server's `RATE_OF_SALES_SOURCES`: a per-week copy of its source,
 * aggregated with `mean` (the source itself sums), giving the average weekly
 * sales that week cover divides by.
 */
export const RATE_OF_SALES_SOURCES: Record<string, string> = {
  RATE_OF_GROSS_SALES: "GIS1",
  RATE_OF_GROSS_SALES_AT_COST: "GS1_COGS",
  RATE_OF_NET_SALES: "NIS3",
  RATE_OF_NET_SALES_AT_COST: "NS3_COGS"
};

/**
 * Week cover: period-end stock over the period's average weekly sales, so the
 * unit stays weeks at every granularity.
 *
 * Kept out of {@link SALES_KPI_DERIVATIONS} because the denominator is a `mean`
 * aggregate, not a sum: only a consumer holding the weekly frame
 * (`temporalAggregation`) can produce it. The summary grid has no week counts,
 * so it shows the server's value there — the same limit as TS / FPOS.
 * Mirrors the server's `WEEK_COVER_INPUTS` / `calculate_week_cover_kpis`.
 */
export const WEEK_COVER_DERIVATIONS: DerivedMetricDef[] = [
  {
    name: "GROSS_WEEK_COVER",
    calc: (v) =>
      coverDivide(v["END_OF_PERIOD_UNITS"] ?? 0, v["RATE_OF_GROSS_SALES"] ?? 0)
  },
  {
    name: "GROSS_WEEK_COVER_AT_COST",
    calc: (v) =>
      coverDivide(
        v["END_OF_PERIOD_AT_COST"] ?? 0,
        v["RATE_OF_GROSS_SALES_AT_COST"] ?? 0
      )
  },
  {
    name: "NET_WEEK_COVER",
    calc: (v) =>
      coverDivide(v["END_OF_PERIOD_UNITS"] ?? 0, v["RATE_OF_NET_SALES"] ?? 0)
  },
  {
    name: "NET_WEEK_COVER_AT_COST",
    calc: (v) =>
      coverDivide(
        v["END_OF_PERIOD_AT_COST"] ?? 0,
        v["RATE_OF_NET_SALES_AT_COST"] ?? 0
      )
  }
];

/**
 * Sign of the planned-markdown EOP correction for the selected markdown_status.
 * A NEW markdown moves units FP->MD, so full-price EOP drops by NEW_MD and
 * markdown EOP rises by it; at total (both / none selected) the buckets cancel,
 * so no correction. Mirrors the server's ``_add_markdown_flows``. Shared by the
 * summary derivations (via buildSalesKpiDerivations) and the weekly engine.
 */
export function markdownFlowSign(
  markdownStatus: string[] | null | undefined
): number {
  if (markdownStatus?.length !== 1) return 0;
  // Bucket values match the server's MarkdownStatus enum.
  if (markdownStatus[0] === "full price") return -1;
  if (markdownStatus[0] === "markdown") return 1;
  return 0;
}

/**
 * The ratios a grid week re-derives from its inputs after a recompute, as
 * `sf_to_gf` and every reload do; the stock balance is the weekly engine's.
 */
export const WEEKLY_SALES_DERIVATIONS = SALES_KPI_DERIVATIONS.filter(
  (definition) => !definition.stockBalanceDerived
);
